import { Button, Image, Stack, Tag, Text } from 'components/ui';
import Link from 'next/link';
import { FC } from 'react';
import { SETTINGS } from 'settings/config';
import styled from 'styled-components';
import { breakpoints } from 'styles/theme';
import { SpecialistWithExpandedRefs } from 'utils/queries';
import { urlForImage } from 'utils/sanity';
import pin from './pin.svg';

export interface SpecialistCardProps {
  data: SpecialistWithExpandedRefs;
}

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;

  * {
    padding: 0 0.25em;
    font-size: 0.75rem;
  }

  > * + * {
    margin-left: var(--space-0);
  }

  @supports (gap: 1px) {
    gap: var(--space-0);

    > * {
      margin-left: 0;
    }
  }
`;

const Location = styled(Text)`
  display: flex;
  align-items: center;

  &::before {
    display: block;
    width: 12px;
    height: 16px;
    margin-right: var(--space-0);
    background-image: url(${pin});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    content: '';
  }
`;

const Grid = styled.div`
  display: grid;
  gap: var(--space-1);

  @media screen and (min-width: ${breakpoints.medium}) {
    grid-template-columns: 4fr 6fr;
    gap: var(--space-3);
    align-items: center;
  }

  @media screen and (min-width: ${breakpoints.large}) {
    grid-template-columns: 2fr 6fr;
  }

  > * {
    /* The image */
    &:nth-child(1) {
      grid-column: 1 / span 1;
    }

    /* The "transparent" container */
    &:nth-child(2) {
      > * {
        /* All elements */
        grid-column: 1 / span 2;

        /* The heading/location */
        &:nth-child(1) {
          grid-column: 2 / span 1;
        }
      }
    }
  }
`;

const Bio = styled(Text)`
  display: -webkit-box;
  overflow: hidden;
  font-size: 0.875rem;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  overflow: hidden;
  background-color: var(--color-grey100);
  border-radius: 10px;
`;

const Name = styled(Text)`
  font-size: var(--font-size-small);
  line-height: var(--line-height-medium);
`;

const ProfileButton = styled(Button)`
  width: fit-content;
`;

const ContentWrapper = styled.div`
  display: contents;

  @media screen and (min-width: ${breakpoints.medium}) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    > * + * {
      margin-top: var(--space-1);
    }

    > * {
      &:last-child {
        margin-top: var(--space-2);
      }
    }
  }

  @media screen and (min-width: ${breakpoints.xLarge}) {
    > * {
      &:last-child {
        margin-top: var(--space-4);
      }
    }
  }
`;

const SpecialistCard: FC<SpecialistCardProps> = ({ data }) => {
  const imageUrl = urlForImage(data.image).width(240).url();
  const location = `${data.location?.city}, ${data.location?.state}`;

  return (
    <Grid>
      <ImageWrapper>
        {imageUrl && (
          <Image
            src={imageUrl}
            objectFit='cover'
            layout='fill'
            alt={data.image?.alt || ''}
          />
        )}
      </ImageWrapper>
      <ContentWrapper>
        <Stack space={0}>
          <Location fontWeight='medium' fontSize='xxxSmall'>
            {location}
          </Location>
          <Name fontSize='small' fontWeight='bold'>
            {data.name}
          </Name>
        </Stack>
        <Bio fontWeight='bold'>{data.specialistType?.name}</Bio>
        <Bio>{data.bio}</Bio>
        <Tags>
          {data.services?.map((service) => (
            <Tag backgroundColor='grey100' key={service._key}>
              {(service as any).service?.name}
            </Tag>
          ))}
        </Tags>
        <Link
          href={`${SETTINGS.routes.specialist.slug}/${data.slug?.current}`}
          passHref
        >
          <ProfileButton as='a'>View Profile</ProfileButton>
        </Link>
      </ContentWrapper>
    </Grid>
  );
};

export default SpecialistCard;
